import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { TrendingUp, TrendingDown, Wallet, Percent, Receipt, DollarSign, Tag } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const PRIMARY = '#22C55E';
const DANGER = '#EF4444';
const BLUE = '#3B82F6';

const KpiCard = ({ title, value, icon: Icon, color = PRIMARY, hint, isPercent = false }) => (
  <Card className="bg-zinc-900 border-zinc-800">
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">{title}</span>
      </div>
      <p className="text-2xl font-bold font-numbers truncate" style={{ color }}>
        {isPercent ? `${(value || 0).toFixed(1)}%` : formatCurrency(value)}
      </p>
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </CardContent>
  </Card>
);

const RankingList = ({ itens, total, color = PRIMARY, testId }) => (
  <div className="space-y-3" data-testid={testId}>
    {itens.length === 0 ? (
      <p className="text-sm text-zinc-500 py-4 text-center">Sem dados no período.</p>
    ) : itens.map((it, i) => {
      const pct = total > 0 ? (it.valor / total) * 100 : 0;
      return (
        <div key={i} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-300 truncate pr-2">{it.nome}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-white font-numbers font-medium">{formatCurrency(it.valor)}</span>
              <span className="text-zinc-500 w-11 text-right text-xs">{pct.toFixed(0)}%</span>
            </div>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
          </div>
        </div>
      );
    })}
  </div>
);

const EvolucaoChart = ({ data, keys, animate = true }) => (
  <div className="h-64">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data}>
        <defs>
          <linearGradient id="gReceita" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={PRIMARY} stopOpacity={0.3} /><stop offset="95%" stopColor={PRIMARY} stopOpacity={0} /></linearGradient>
          <linearGradient id="gDespesa" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={DANGER} stopOpacity={0.3} /><stop offset="95%" stopColor={DANGER} stopOpacity={0} /></linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
        <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11 }} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11 }}
          tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
        <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} formatter={(v) => [formatCurrency(v)]} />
        {keys.includes('receita') && <Area type="linear" dataKey="receita" name="Receita" stroke={PRIMARY} strokeWidth={2} fill="url(#gReceita)" isAnimationActive={animate} />}
        {keys.includes('despesa') && <Area type="linear" dataKey="despesa" name="Despesa" stroke={DANGER} strokeWidth={2} fill="url(#gDespesa)" isAnimationActive={animate} />}
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const SectionCard = ({ title, children }) => (
  <Card className="bg-zinc-900 border-zinc-800">
    <CardContent className="p-6">
      <h3 className="text-base font-medium text-white mb-6">{title}</h3>
      {children}
    </CardContent>
  </Card>
);

const BarBlock = ({ data, color = PRIMARY, animate }) => (
  <div style={{ height: Math.max(data.length * 44 + 20, 160) }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11 }}
          tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
        <YAxis type="category" dataKey="name" width={130} axisLine={false} tickLine={false} tick={{ fill: '#a1a1aa', fontSize: 12 }} />
        <Tooltip cursor={{ fill: '#27272a55' }} contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} formatter={(v) => [formatCurrency(v)]} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={animate}>
          {data.map((e, i) => <Cell key={i} fill={color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

// ============ PAINEL EXECUTIVO ============
export const PainelExecutivo = ({ data, animate }) => {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="painel-executivo">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Faturamento" value={data.total_faturamento} icon={TrendingUp} color={PRIMARY} hint={`Média ${formatCurrency(data.media_faturamento_mensal)}/mês`} />
        <KpiCard title="Despesas" value={data.total_despesa} icon={TrendingDown} color={DANGER} hint={`Média ${formatCurrency(data.media_despesa_mensal)}/mês`} />
        <KpiCard title="Lucro" value={data.lucro} icon={Wallet} color={data.lucro >= 0 ? PRIMARY : DANGER} />
        <KpiCard title="Margem" value={data.margem} icon={Percent} color={data.margem >= 0 ? PRIMARY : DANGER} isPercent hint={`${data.periodo?.meses_count} mês(es)`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SectionCard title="Evolução: Receita × Despesa">
            <EvolucaoChart data={data.evolucao_mensal} keys={['receita', 'despesa']} animate={animate} />
          </SectionCard>
        </div>
        <SectionCard title="Top Categorias de Despesa">
          <RankingList itens={(data.despesa_por_categoria || []).slice(0, 6)} total={data.total_despesa} color={DANGER} testId="exec-ranking-despesas" />
        </SectionCard>
      </div>
    </div>
  );
};

// ============ PAINEL DE DESPESAS ============
export const PainelDespesas = ({ data, animate }) => {
  if (!data) return null;
  const pieData = (data.despesa_por_categoria || []).map((c) => ({ name: c.nome, value: c.valor }));
  return (
    <div className="space-y-6" data-testid="painel-despesas">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard title="Despesa Total" value={data.total_despesa} icon={TrendingDown} color={DANGER} />
        <KpiCard title="Média Mensal" value={data.media_despesa_mensal} icon={Receipt} color={DANGER} hint={`${data.periodo?.meses_count} mês(es)`} />
        <KpiCard title="Categorias" value={data.despesa_por_categoria?.length || 0} icon={Tag} color={BLUE} hint="categorias com gasto" isPercent={false} />
      </div>
      <SectionCard title="Despesas por Categoria">
        {pieData.length > 0 ? <BarBlock data={pieData} color={DANGER} animate={animate} /> : <p className="text-sm text-zinc-500 py-10 text-center">Sem despesas no período.</p>}
      </SectionCard>
      <SectionCard title="Evolução das Despesas">
        <EvolucaoChart data={data.evolucao_mensal} keys={['despesa']} animate={animate} />
      </SectionCard>
    </div>
  );
};

// ============ PAINEL DE FATURAMENTO ============
export const PainelFaturamento = ({ data, animate }) => {
  if (!data) return null;
  const pieData = (data.faturamento_por_fonte || []).map((c) => ({ name: c.nome, value: c.valor }));
  return (
    <div className="space-y-6" data-testid="painel-faturamento">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard title="Faturamento Total" value={data.total_faturamento} icon={TrendingUp} color={PRIMARY} />
        <KpiCard title="Média Mensal" value={data.media_faturamento_mensal} icon={DollarSign} color={PRIMARY} hint={`${data.periodo?.meses_count} mês(es)`} />
        <KpiCard title="Ticket Médio" value={data.ticket_medio} icon={Receipt} color={BLUE} hint={`${data.num_lancamentos_receita} lançamento(s)`} />
      </div>
      <SectionCard title="Faturamento por Fonte">
        {pieData.length > 0 ? <BarBlock data={pieData} color={PRIMARY} animate={animate} /> : <p className="text-sm text-zinc-500 py-10 text-center">Sem faturamento no período.</p>}
      </SectionCard>
      <SectionCard title="Evolução do Faturamento">
        <EvolucaoChart data={data.evolucao_mensal} keys={['receita']} animate={animate} />
      </SectionCard>
    </div>
  );
};
